import { FunctionComponent, useEffect, useState } from 'react'
import { Box, TextField, Button, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import colors from '../../colors'
import { SPACING } from '../../constants'

interface FeeSettings {
  coreTierServiceFee: string
  proTierServiceFee: string
  proTrialPeriodDays: number
}

/**
 * Platform-wide defaults applied to organisations that sign up from here on.
 *
 * Service fees never touch an organisation that already exists - its rate was
 * fixed at signup. The Pro trial is applied when a subscription is created, so
 * it likewise only affects new ones.
 *
 * Super admin only, enforced server side.
 */
const PlatformFeesSection: FunctionComponent = () => {
  const styles = useStyles()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [coreFee, setCoreFee] = useState('')
  const [proFee, setProFee] = useState('')
  const [trialDays, setTrialDays] = useState('0')

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get<FeeSettings>('/super_admin/platform_settings')
        setCoreFee(String(response.data.coreTierServiceFee))
        setProFee(String(response.data.proTierServiceFee))
        setTrialDays(String(response.data.proTrialPeriodDays ?? 0))
      } catch {
        toast.error('Could not load platform fees')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await axios.patch('/super_admin/platform_settings', {
        coreTierServiceFee: coreFee,
        proTierServiceFee: proFee,
        proTrialPeriodDays: trialDays,
      })
      toast.success('Platform defaults updated')
    } catch (error: any) {
      toast.error(
        error.response?.data?.message
        || error.response?.data?.error
        || 'Failed to update platform defaults'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box className={styles.section}>
      <h2 className={styles.heading}>Platform defaults</h2>
      <p className={styles.description}>
        The service fee applied to organisations that sign up from now on.
        Existing organisations keep the rate they were given at signup — changing
        these never alters what anyone is already being charged.
      </p>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Box className={styles.form}>
          <Box className={styles.fields}>
            <TextField
              label="Core tier (%)"
              variant="outlined"
              type="number"
              value={coreFee}
              onChange={(e) => setCoreFee(e.target.value)}
            />
            <TextField
              label="Pro tier (%)"
              variant="outlined"
              type="number"
              value={proFee}
              onChange={(e) => setProFee(e.target.value)}
            />
            <TextField
              label="Pro free trial (days)"
              variant="outlined"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
              helperText="0 means no trial"
            />
          </Box>

          <p className={styles.description}>
            A trial delays the first charge on new Pro subscriptions, and is
            advertised on the signup page. Note that while a trial is running
            there is no payment to decline, so a bad card will not be caught at
            signup the way it is without one.
          </p>
          <Button
            type="button"
            disableElevation
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', alignSelf: 'flex-start' }}
          >
            {saving ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
            Save
          </Button>
        </Box>
      )}
    </Box>
  )
}

const useStyles = makeStyles({
  section: {
    border: `1px solid ${colors.GREY_5}`,
    borderRadius: 12,
    padding: SPACING * 3,
    background: colors.WHITE,
    maxWidth: 560,
  },
  heading: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: colors.STITCH_BLACK,
  },
  description: {
    marginTop: SPACING,
    marginBottom: SPACING * 2,
    fontSize: 13,
    lineHeight: 1.5,
    color: colors.GREY_2,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING * 2,
  },
  fields: {
    display: 'flex',
    gap: SPACING * 2,
  },
})

export default PlatformFeesSection
